import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { MrsLink } from '@/components/MrsLink';
import type { RecentMrsDto } from '../types';
import { DocumentStatus } from '../types';

interface RecentMrsTableProps {
  data: RecentMrsDto[];
}

const statusConfig: Record<DocumentStatus, { label: string; variant: 'default' | 'secondary' | 'outline' | 'destructive' }> = {
  [DocumentStatus.Draft]: { label: 'Draft', variant: 'outline' },
  [DocumentStatus.Open]: { label: 'Open', variant: 'outline' },
  [DocumentStatus.Closed]: { label: 'Served', variant: 'default' },
  [DocumentStatus.Cancelled]: { label: 'Cancelled', variant: 'destructive' },
};

export function RecentMrsTable({ data }: RecentMrsTableProps) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">Recent Material Requests</CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/50">
              <tr>
                <th className="px-4 py-2 text-left font-medium">MRS #</th>
                <th className="px-4 py-2 text-left font-medium">Requestor</th>
                <th className="px-4 py-2 text-center font-medium">Items</th>
                <th className="px-4 py-2 text-center font-medium">Age</th>
                <th className="px-4 py-2 text-center font-medium">Status</th>
              </tr>
            </thead>
            <tbody>
              {data.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-muted-foreground">
                    No recent requests
                  </td>
                </tr>
              ) : (
                data.map((mrs) => {
                  const config = statusConfig[mrs.documentStatus] ?? { label: 'Unknown', variant: 'outline' as const };
                  return (
                    <tr
                      key={mrs.id}
                      className={`border-b hover:bg-muted/50 ${mrs.isOverdue ? 'bg-destructive/10' : ''}`}
                    >
                      <td className="px-4 py-2 font-medium">
                        <MrsLink mrsId={mrs.id} mrsNumber={mrs.mrsNumber} />
                      </td>
                      <td className="px-4 py-2">{mrs.requestorFullName}</td>
                      <td className="px-4 py-2 text-center">{mrs.itemCount}</td>
                      <td className="px-4 py-2 text-center">
                        <span className={mrs.isOverdue ? 'text-destructive font-medium' : ''}>
                          {mrs.ageDays}d
                        </span>
                      </td>
                      <td className="px-4 py-2 text-center">
                        <Badge variant={config.variant}>{config.label}</Badge>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}
